// PFM-2564: thin wrapper around POST /api/v1/share-hashes + POST /api/v1/shareables.
// Returns the full https URL the user can copy or email to a recipient.
//
// Two-step flow because backend models the hash separately from the
// thing-being-shared: a ShareHash carries auth metadata (token, expiry, scopes),
// and a Shareable joins it to a single Assessment/Task/Document.

import sendData from 'redux/actions/sendData';
import { buildShareUrl, SHARE_KINDS } from './shareLinkConfig';

const KIND_TO_SCOPE = {
  assessment: 'assessments:read',
  task: 'tasks:read',
  document: 'documents:read',
};

const randomHash = () => {
  // 24 char URL-safe random — readable, hard to guess. Backend doesn't require any
  // particular shape, just uniqueness inside the share-hashes table.
  const bytes = new Uint8Array(18);
  (window.crypto || window.msCrypto).getRandomValues(bytes);
  return btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const daysFromNowIso = (days) => {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() + Number(days || 7));
  return d.toISOString().replace(/\.\d{3}Z$/, 'Z');
};

// scope: { kind: 'assessment' | 'task' | 'document', id, courseId? }
// opts:  { expirationDays, name }
export const createShareLink = async (scope, opts = {}) => {
  if (!scope || !SHARE_KINDS.includes(scope.kind) || !scope.id) {
    throw new Error('createShareLink: invalid scope');
  }
  const expirationDays = Number(opts.expirationDays || 7);
  const hash = randomHash();
  const expiresAt = daysFromNowIso(expirationDays);
  const tokenScope = KIND_TO_SCOPE[scope.kind] || 'assessments:read';

  const hashResp = await sendData({
    url: 'share-hashes',
    method: 'POST',
    data: {
      data: {
        type: 'share-hashes',
        attributes: {
          name: opts.name || `${scope.kind} ${scope.id}`,
          hash,
          scopes: [tokenScope],
          'expires-at': expiresAt,
        },
      },
    },
  });

  const shareHashId = hashResp?.data?.data?.id;
  if (!shareHashId) throw new Error('createShareLink: backend did not return a share-hash id');

  // Document-type is 'edition_document' (underscore) on the backend side — it must
  // match the backend morph map exactly; the dashed form is stored verbatim and
  // breaks edition reads under /sharehash/v1 (PFM-2641).
  const backendType = scope.kind === 'document' ? 'edition_document' : scope.kind;

  await sendData({
    url: 'shareables',
    method: 'POST',
    data: {
      data: {
        type: 'shareables',
        attributes: {
          'shareable-type': backendType,
          'shareable-id': Number(scope.id),
        },
        relationships: {
          'share-hash': {
            data: { type: 'share-hashes', id: shareHashId },
          },
        },
      },
    },
  });

  const path = buildShareUrl(hash, scope);
  return {
    hash,
    shareHashId,
    expiresAt,
    url: `${window.location.origin}${path}`,
  };
};
